import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { api, API, downloadPdf as downloadPdfFile } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isLate, isEarly } from "@/lib/utils";
import { PLAN_KEYS, PLAN_LABELS, planAmount, hasAnyPlans } from "@/lib/plans";
import { ArrowLeft, Download, IdCard, CalendarDays, Loader2, FileText, GraduationCap, Wallet, Eye, MessageCircle, Mail, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
const WEEKDAYS_ES = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];

function ProfilePhoto({ photoPath, name }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    let blobUrl, cancelled = false;
    (async () => {
      if (!photoPath) return;
      try {
        const r = await api.get(`/files/${photoPath}`, { responseType: "blob" });
        if (cancelled) return;
        blobUrl = URL.createObjectURL(r.data);
        setSrc(blobUrl);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [photoPath]);

  if (!src) {
    return (
      <div className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl bg-nardo-100 flex items-center justify-center text-navy font-display font-extrabold text-4xl border border-nardo-200" data-testid="profile-photo-placeholder">
        {name?.[0]?.toUpperCase() || "?"}
      </div>
    );
  }
  return <img src={src} alt={name} className="w-28 h-28 sm:w-36 sm:h-36 rounded-xl object-cover border border-nardo-200" data-testid="profile-photo" />;
}

function fmtTime(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true });
  } catch { return "—"; }
}

function Heatmap({ daysMap }) {
  // Build last 365 days grid (GitHub-style): columns = weeks, rows = weekday (Sun..Sat)
  const cells = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const start = new Date(today);
    start.setDate(start.getDate() - 364);
    // align start to Sunday
    start.setDate(start.getDate() - start.getDay());
    const weeks = [];
    let cur = new Date(start);
    while (cur <= today) {
      const week = [];
      for (let d = 0; d < 7; d++) {
        const iso = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, "0")}-${String(cur.getDate()).padStart(2, "0")}`;
        const inRange = cur >= new Date(today.getFullYear(), today.getMonth(), today.getDate() - 364) && cur <= today;
        week.push({ iso, count: daysMap[iso]?.count || 0, present: !!daysMap[iso], inRange, month: cur.getMonth(), date: cur.getDate() });
        cur.setDate(cur.getDate() + 1);
      }
      weeks.push(week);
    }
    return weeks;
  }, [daysMap]);

  const color = (c) => {
    if (c === 0) return "bg-nardo-100";
    if (c <= 2) return "bg-navy/30";
    if (c <= 4) return "bg-navy/60";
    return "bg-navy";
  };

  // Month labels row
  const monthLabels = useMemo(() => {
    return cells.map((week, i) => {
      const first = week[0];
      const prev = cells[i - 1]?.[0];
      if (i === 0 || (prev && new Date(prev.iso).getMonth() !== new Date(first.iso).getMonth())) {
        return MONTHS_ES[new Date(first.iso).getMonth()];
      }
      return "";
    });
  }, [cells]);

  return (
    <div className="overflow-x-auto pb-2" data-testid="attendance-heatmap">
      <div className="inline-flex flex-col gap-1 min-w-max">
        <div className="flex gap-[3px] ml-8">
          {monthLabels.map((m, i) => (
            <div key={i} className="w-[11px] text-[9px] text-nardo-500">{m}</div>
          ))}
        </div>
        <div className="flex gap-[3px]">
          <div className="flex flex-col gap-[3px] mr-1 w-7">
            {WEEKDAYS_ES.map((d, i) => (
              <div key={i} className="h-[11px] text-[8px] text-nardo-400 leading-[11px]">{i % 2 === 1 ? d : ""}</div>
            ))}
          </div>
          {cells.map((week, wi) => (
            <div key={wi} className="flex flex-col gap-[3px]">
              {week.map((cell, di) => (
                <div
                  key={di}
                  title={cell.inRange ? `${cell.iso}: ${cell.count} registro(s)` : ""}
                  className={`w-[11px] h-[11px] rounded-sm ${cell.inRange ? color(cell.count) : "bg-transparent"}`}
                />
              ))}
            </div>
          ))}
        </div>
        <div className="flex items-center gap-1 mt-2 ml-8 text-[9px] text-nardo-500">
          <span>Menos</span>
          <span className="w-[11px] h-[11px] rounded-sm bg-nardo-100" />
          <span className="w-[11px] h-[11px] rounded-sm bg-navy/30" />
          <span className="w-[11px] h-[11px] rounded-sm bg-navy/60" />
          <span className="w-[11px] h-[11px] rounded-sm bg-navy" />
          <span>Más</span>
        </div>
      </div>
    </div>
  );
}

export default function PersonProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isTeacher = location.pathname.startsWith("/teachers");
  const lateThreshold = isTeacher ? user?.late_after_teacher : user?.late_after;
  const exitThreshold = isTeacher ? user?.exit_before_teacher : user?.exit_before;
  const [data, setData] = useState(null);
  const [justifs, setJustifs] = useState([]);
  const [stmt, setStmt] = useState(null);
  const [recPeriod, setRecPeriod] = useState("all");
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(""); // YYYY-MM filter, "" = all
  const LEVELS = ["Preescolar", "Primaria baja", "Primaria alta", "Primaria", "Secundaria", "Preparatoria"];
  const [editOpen, setEditOpen] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [ef, setEf] = useState({ full_name: "", grade: "", level: "", nfc_uid: "", whatsapp: "", blood_type: "", plan: "", monthly_fee: "", enrollment_fee: "", schId: "" });
  const [eTutors, setETutors] = useState([]);
  const scholarships = user?.scholarships || [];

  const openEdit = () => {
    const b = person?.billing || {};
    const sch = scholarships.find((s) => (b.scholarship_name || "") && s.name === b.scholarship_name);
    setEf({
      full_name: person?.full_name || "",
      grade: person?.grade || "",
      level: person?.level || "",
      nfc_uid: person?.nfc_uid || "",
      whatsapp: person?.whatsapp || "",
      blood_type: person?.blood_type || "",
      plan: b.plan || "",
      monthly_fee: b.monthly_fee != null ? String(b.monthly_fee) : "",
      enrollment_fee: b.enrollment_fee != null ? String(b.enrollment_fee) : "",
      schId: sch ? sch.id : "",
    });
    setETutors((person?.billing?.tutors || stmt?.tutors || []).map((t) => ({ name: t.name || "", whatsapp: t.whatsapp || "", email: t.email || "", relation: t.relation || "" })));
    setEditOpen(true);
  };
  const eUpdateTutor = (i, k, v) => setETutors((ts) => ts.map((t, idx) => (idx === i ? { ...t, [k]: v } : t)));
  const eAddTutor = () => setETutors((ts) => [...ts, { name: "", whatsapp: "", email: "", relation: "" }]);
  const eRemoveTutor = (i) => setETutors((ts) => ts.filter((_, idx) => idx !== i));

  const saveEdit = async () => {
    if (!ef.full_name.trim()) { toast.error("El nombre es obligatorio"); return; }
    setSavingEdit(true);
    try {
      const body = {
        full_name: ef.full_name,
        grade: ef.grade,
        nfc_uid: ef.nfc_uid,
      };
      if (isTeacher) { body.whatsapp = ef.whatsapp; }
      if (!isTeacher) { body.level = ef.level; body.blood_type = ef.blood_type; }
      await api.put(`/students/${id}`, body);
      if (!isTeacher) {
        const b = person?.billing || {};
        const sch = scholarships.find((s) => s.id === ef.schId);
        await api.put(`/students/${id}/billing`, {
          tutors: eTutors,
          address: b.address || "",
          plan: ef.plan || "",
          monthly_fee: Number(ef.monthly_fee) || 0,
          enrollment_fee: Number(ef.enrollment_fee) || 0,
          scholarship_pct: sch ? Number(sch.pct) || 0 : 0,
          scholarship_name: sch ? sch.name : "",
        });
      }
      toast.success("Información actualizada");
      setEditOpen(false);
      // recargar ficha y estado de cuenta
      const { data: hist } = await api.get(`/people/${id}/history`);
      setData(hist);
      if (!isTeacher) {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const s = await api.get(`/students/${id}/statement`, { params: { tz } });
          setStmt(s.data);
        } catch { /* sin cobranza */ }
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo actualizar");
    } finally { setSavingEdit(false); }
  };


  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/people/${id}/history`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
      try {
        const r = await api.get("/direccion/justifications");
        if (!cancelled) setJustifs((r.data || []).filter((j) => j.student_id === id));
      } catch { /* staff sin acceso o sin justificantes */ }
      if (!isTeacher) {
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          const s = await api.get(`/students/${id}/statement`, { params: { tz } });
          if (!cancelled) setStmt(s.data);
        } catch { /* sin cobranza */ }
      }
    })();
    return () => { cancelled = true; };
  }, [id, isTeacher]);

  const monthSummary = useMemo(() => {
    const now = new Date();
    const ym = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    const md = (data?.days || []).filter((d) => d.date.startsWith(ym));
    const present = md.filter((d) => d.first_in || d.last_out).length;
    const late = md.filter((d) => d.first_in && isLate(d.first_in, lateThreshold)).length;
    let weekdays = 0;
    for (let day = 1; day <= now.getDate(); day++) {
      const wd = new Date(now.getFullYear(), now.getMonth(), day).getDay();
      if (wd !== 0 && wd !== 6) weekdays++;
    }
    const pct = weekdays > 0 ? Math.min(100, Math.round((present / weekdays) * 100)) : 0;
    return { present, late, weekdays, pct };
  }, [data, lateThreshold]);

  const daysMap = useMemo(() => {
    const m = {};
    (data?.days || []).forEach((d) => { m[d.date] = d; });
    return m;
  }, [data]);

  const monthOptions = useMemo(() => {
    const set = new Set((data?.days || []).map((d) => d.date.slice(0, 7)));
    return Array.from(set).sort().reverse();
  }, [data]);

  const filteredDays = useMemo(() => {
    if (!month) return data?.days || [];
    return (data?.days || []).filter((d) => d.date.startsWith(month));
  }, [data, month]);

  const downloadPdf = async () => {
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const params = month ? { month, tz } : { tz };
      const r = await api.get(`/people/${id}/pdf`, { params, responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ficha_${(person?.full_name || "persona").replace(/\s+/g, "_")}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Error al exportar PDF");
    }
  };

  const openParentPreview = async () => {
    try {
      const { data } = await api.post(`/students/${id}/parent-token`);
      window.open(`/padres?preview=${encodeURIComponent(data.token)}`, "_blank");
    } catch {
      toast.error("No se pudo abrir la vista previa");
    }
  };

  const receiptUrl = (p) => `${API}/api/recibo/${p.receipt_token}`;
  const sendReceiptWhatsApp = (p, t) => {
    const msg = `Hola${t.name ? " " + t.name : ""}, le compartimos el comprobante de pago (${p.concept}${p.period ? " " + p.period : ""}) de ${data?.full_name || "su hijo/a"}: ${receiptUrl(p)}`;
    window.open(`https://wa.me/${t.whatsapp}?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const sendReceiptEmail = (p, t) => {
    const subject = `Comprobante de pago ${p.folio || ""}`.trim();
    const body = `Hola${t.name ? " " + t.name : ""},\n\nLe compartimos el comprobante de pago (${p.concept}${p.period ? " " + p.period : ""}) de ${data?.full_name || "su hijo/a"}:\n${receiptUrl(p)}\n\nSaludos.`;
    window.location.href = `mailto:${t.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const [busyMonth, setBusyMonth] = useState(null);
  const downloadMonth = async (ym) => {
    if (busyMonth) return;
    setBusyMonth(ym);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await downloadPdfFile(`/people/${id}/pdf`, { params: { month: ym, tz }, filename: `ficha_${(person?.full_name || "persona").replace(/\s+/g, "_")}_${ym}.pdf` });
      toast.success(`PDF de ${ym} generado`);
    } catch {
      toast.error("No se pudo generar el PDF del mes");
    } finally {
      setBusyMonth(null);
    }
  };

  // ---- Expediente por periodo (Hoy / Semana / Mes / Ciclo) ----
  const [expPeriod, setExpPeriod] = useState("hoy");
  const [expBusy, setExpBusy] = useState(false);
  const periodRange = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    if (expPeriod === "hoy") return { start: today, end: new Date(today.getTime() + 86400000) };
    if (expPeriod === "semana") {
      const dow = (today.getDay() + 6) % 7; // lunes=0
      const start = new Date(today); start.setDate(start.getDate() - dow);
      return { start, end: new Date(start.getTime() + 7 * 86400000) };
    }
    if (expPeriod === "mes") {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      return { start, end };
    }
    // ciclo escolar Ago–Jul
    const y = today.getMonth() >= 7 ? today.getFullYear() : today.getFullYear() - 1;
    return { start: new Date(y, 7, 1), end: new Date(y + 1, 7, 1) };
  }, [expPeriod]);

  const expSummary = useMemo(() => {
    const days = (data?.days || []).filter((d) => {
      const dt = new Date(d.date + "T00:00:00");
      return dt >= periodRange.start && dt < periodRange.end;
    });
    let present = 0, late = 0, early = 0;
    days.forEach((d) => {
      if (d.first_in || d.last_out) present++;
      if (d.first_in && isLate(d.first_in, lateThreshold)) late++;
      if (d.last_out && isEarly(d.last_out, exitThreshold)) early++;
    });
    return { present, late, early, records: days.reduce((s, d) => s + (d.count || 0), 0) };
  }, [data, periodRange, lateThreshold, exitThreshold]);

  const periodJustifs = useMemo(() => {
    return justifs
      .filter((j) => {
        const dt = new Date(j.date + "T00:00:00");
        return dt >= periodRange.start && dt < periodRange.end;
      })
      .sort((a, b) => b.date.localeCompare(a.date));
  }, [justifs, periodRange]);

  const downloadExpediente = async () => {
    if (expBusy) return;
    setExpBusy(true);
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      await downloadPdfFile(`/people/${id}/pdf`, { params: { period: expPeriod, tz }, filename: `expediente_${(person?.full_name || "persona").replace(/\s+/g, "_")}_${expPeriod}.pdf` });
      toast.success("Expediente PDF generado");
    } catch {
      toast.error("No se pudo generar el expediente");
    } finally {
      setExpBusy(false);
    }
  };

  const [boleta, setBoleta] = useState(null);
  const [boletaOpen, setBoletaOpen] = useState(false);
  const [boletaBusy, setBoletaBusy] = useState(false);
  const openBoleta = async () => {
    setBoletaOpen(true);
    if (boleta) return;
    setBoletaBusy(true);
    try {
      const { data } = await api.get(`/students/${id}/boleta`);
      setBoleta(data);
    } catch {
      toast.error("No se pudo cargar el expediente de calificaciones");
    } finally {
      setBoletaBusy(false);
    }
  };
  const downloadBoleta = async () => {
    try {
      await downloadPdfFile(`/students/${id}/boleta.pdf`, { filename: `boleta_${(person?.full_name || "alumno").replace(/\s+/g, "_")}.pdf` });
      toast.success("Boleta PDF generada");
    } catch {
      toast.error("No se pudo generar la boleta");
    }
  };


  const person = data?.person;

  if (loading) {
    return <div className="max-w-6xl mx-auto py-20 text-center text-nardo-500" data-testid="profile-loading">Cargando ficha…</div>;
  }
  if (!person) {
    return <div className="max-w-6xl mx-auto py-20 text-center text-nardo-500">Persona no encontrada.</div>;
  }

  return (
    <div className="max-w-6xl mx-auto" data-testid="person-profile-page">
      <button
        data-testid="profile-back-button"
        onClick={() => navigate(isTeacher ? "/teachers" : "/students")}
        className="inline-flex items-center gap-2 text-sm text-nardo-600 hover:text-navy mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a {isTeacher ? "Docentes y Personal" : "Alumnos"}
      </button>

      {/* Header card */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 sm:p-8 relative mb-4">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <div className="flex flex-col sm:flex-row gap-6 items-start">
          <ProfilePhoto photoPath={person.photo_path} name={person.full_name} />
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.3em] text-navy-light mb-2 flex items-center gap-2">
              <span className="w-8 h-px bg-navy" /> {isTeacher ? "Docente" : "Alumno"}
            </div>
            <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-navy" data-testid="profile-name">{person.full_name}</h1>
            {!isTeacher && (() => { const t = (person?.billing?.tutors || []).find((x) => (x.name || "").trim()); if (!t) return null; const rel = (t.relation || "").trim(); return <div className="text-sm text-nardo-500 mt-1" data-testid="profile-tutor-label">Tutor: {rel ? `${t.name} (${rel.toLowerCase()})` : t.name}</div>; })()}
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4 text-sm">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">{isTeacher ? "Materia / Cargo" : "Grado"}</div>
                <div className="text-navy font-medium mt-1" data-testid="profile-grade">{person.grade || "—"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 flex items-center gap-1"><IdCard className="w-3 h-3" /> UID NFC</div>
                <div className="text-navy font-mono mt-1" data-testid="profile-uid">{person.nfc_uid || "Sin UID"}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Días con asistencia</div>
                <div className="text-navy font-display font-bold mt-1" data-testid="profile-days-present">{data.days_present}</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Total registros</div>
                <div className="text-navy font-display font-bold mt-1" data-testid="profile-total-records">{data.total_records}</div>
              </div>
            </div>
          </div>
          <button
            data-testid="profile-edit-info"
            onClick={openEdit}
            className="inline-flex items-center gap-2 border border-navy/30 text-navy hover:border-navy hover:bg-nardo-50 px-5 py-2.5 rounded-md transition-colors text-sm font-medium shrink-0"
          >
            <Pencil className="w-4 h-4" /> Editar información
          </button>
          {isTeacher && person?.whatsapp && (
            <a
              data-testid="profile-whatsapp"
              href={`https://wa.me/${person.whatsapp}?text=${encodeURIComponent(`Hola ${person.full_name || ""}, `)}`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 border border-emerald-400 text-emerald-700 hover:bg-emerald-50 px-5 py-2.5 rounded-md transition-colors text-sm font-medium shrink-0"
            >
              <MessageCircle className="w-4 h-4" /> Enviar comunicado
            </a>
          )}
          <button
            data-testid="profile-export-pdf"
            onClick={downloadPdf}
            className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md transition-colors text-sm font-medium shrink-0"
          >
            <Download className="w-4 h-4" /> Exportar PDF
          </button>
          {!isTeacher && (
            <button
              data-testid="profile-parent-preview"
              onClick={openParentPreview}
              className="inline-flex items-center gap-2 border border-navy/30 text-navy hover:border-navy px-5 py-2.5 rounded-md transition-colors text-sm font-medium shrink-0"
            >
              <Eye className="w-4 h-4" /> Ver como lo ve el papá
            </button>
          )}
        </div>
      </div>

      {/* Resumen visual (estilo Portal de Padres) */}
      <div className="grid sm:grid-cols-3 gap-3 mb-4" data-testid="profile-summary">
        <div className="bg-white border border-nardo-200 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Asistencia del mes</div>
          <div className={`font-display font-extrabold text-3xl mt-1 ${monthSummary.pct >= 90 ? "text-emerald-600" : monthSummary.pct >= 75 ? "text-orange-500" : "text-red-600"}`} data-testid="profile-summary-attendance">{monthSummary.pct}%</div>
          <div className="text-xs text-nardo-400 mt-0.5">{monthSummary.present} de {monthSummary.weekdays} días hábiles</div>
        </div>
        <div className="bg-white border border-nardo-200 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Puntualidad</div>
          <div className="font-display font-extrabold text-3xl mt-1 text-navy">{monthSummary.present - monthSummary.late}<span className="text-base text-nardo-400 font-normal"> a tiempo</span></div>
          <div className="text-xs text-nardo-400 mt-0.5">{monthSummary.late} con retardo este mes</div>
        </div>
        <div className="bg-white border border-nardo-200 rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500 flex items-center gap-1"><Wallet className="w-3 h-3" /> Saldo actual</div>
          {isTeacher ? (
            <div className="font-display font-extrabold text-3xl mt-1 text-nardo-300">—</div>
          ) : (
            <>
              <div className={`font-display font-extrabold text-3xl mt-1 ${stmt && stmt.balance > 0 ? "text-orange-600" : "text-emerald-600"}`} data-testid="profile-summary-balance">{stmt ? money(stmt.balance) : "—"}</div>
              <div className="text-xs text-nardo-400 mt-0.5">{stmt ? (stmt.balance > 0 ? "Pendiente de pago" : "Al corriente") : "Sin cobranza"}</div>
            </>
          )}
        </div>
      </div>

      {!isTeacher && (
        <div className="bg-white border border-nardo-200 rounded-md p-6 relative mb-4" data-testid="boleta-card">
          <div className="absolute top-0 left-0 h-[2px] w-16 bg-emerald-500" />
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <GraduationCap className="w-4 h-4 text-emerald-600" />
              <h3 className="font-display font-semibold text-lg text-navy">Expediente de calificaciones</h3>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={openBoleta} data-testid="boleta-open-btn" className="inline-flex items-center gap-2 text-sm font-medium border border-navy/25 text-navy hover:bg-navy/5 px-4 py-2 rounded-lg transition-colors">
                <FileText className="w-4 h-4" /> Ver boleta
              </button>
              <button onClick={downloadBoleta} data-testid="boleta-download-btn" className="inline-flex items-center gap-2 text-sm font-medium bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg transition-colors">
                <Download className="w-4 h-4" /> Descargar PDF
              </button>
            </div>
          </div>
          <p className="text-sm text-nardo-500 mt-2">Boleta con las calificaciones por trimestre de cada materia (capturadas por los docentes en el Libro de Calificaciones). Aparte del expediente normal del alumno.</p>
        </div>
      )}

      {boletaOpen && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto" onClick={() => setBoletaOpen(false)} data-testid="boleta-modal">
          <div className="bg-white border border-nardo-200 rounded-md w-full max-w-3xl p-6 relative my-8" onClick={(e) => e.stopPropagation()}>
            <button onClick={() => setBoletaOpen(false)} className="absolute top-4 right-4 text-nardo-500 hover:text-navy" data-testid="boleta-close"><X className="w-4 h-4" /></button>
            <div className="absolute top-0 left-0 h-[2px] w-16 bg-emerald-500" />
            <h2 className="font-display font-bold text-xl text-navy mb-1">Boleta de calificaciones</h2>
            <p className="text-sm text-nardo-500 mb-4">{person?.full_name} · {person?.grade || "—"}</p>
            {boletaBusy ? (
              <div className="py-16 text-center text-nardo-500 text-sm"><Loader2 className="w-5 h-5 animate-spin inline mr-2" /> Cargando…</div>
            ) : !boleta || (boleta.subjects || []).length === 0 ? (
              <div className="py-12 text-center text-nardo-500 text-sm">Aún no hay calificaciones capturadas para este alumno.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-nardo-200">
                  <thead>
                    <tr className="bg-navy text-white text-[11px] uppercase tracking-wider">
                      <th className="text-left px-3 py-2">Materia</th>
                      <th className="text-left px-3 py-2">Docente</th>
                      <th className="px-3 py-2">{boleta.term_labels?.t1 || "Trim. 1"}</th>
                      <th className="px-3 py-2">{boleta.term_labels?.t2 || "Trim. 2"}</th>
                      <th className="px-3 py-2">{boleta.term_labels?.t3 || "Trim. 3"}</th>
                      <th className="px-3 py-2 bg-navy-light">Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(boleta.subjects || []).map((s, i) => {
                      const pass = boleta.pass_grade ?? 7;
                      const cell = (v) => v === null || v === undefined ? <span className="text-nardo-300">—</span> : <span className={Number(v) < pass ? "text-red-600 font-semibold" : "text-emerald-600 font-semibold"}>{v}</span>;
                      return (
                        <tr key={i} className={i % 2 ? "bg-nardo-50" : ""} data-testid={`boleta-row-${i}`}>
                          <td className="px-3 py-2 text-navy font-medium">{s.subject}</td>
                          <td className="px-3 py-2 text-nardo-500">{s.docente_name}</td>
                          <td className="px-3 py-2 text-center">{cell(s.terms?.t1)}</td>
                          <td className="px-3 py-2 text-center">{cell(s.terms?.t2)}</td>
                          <td className="px-3 py-2 text-center">{cell(s.terms?.t3)}</td>
                          <td className="px-3 py-2 text-center bg-nardo-50">{cell(s.avg)}</td>
                        </tr>
                      );
                    })}
                    <tr className="bg-emerald-50 font-bold text-navy">
                      <td className="px-3 py-2" colSpan={5}>Promedio general</td>
                      <td className="px-3 py-2 text-center" data-testid="boleta-general-avg">{boleta.general_avg ?? "—"}</td>
                    </tr>
                  </tbody>
                </table>
                <div className="mt-4 flex justify-end">
                  <button onClick={downloadBoleta} data-testid="boleta-modal-download" className="inline-flex items-center gap-2 text-sm font-medium bg-navy hover:bg-navy-light text-white px-4 py-2 rounded-lg"><Download className="w-4 h-4" /> Descargar PDF</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comprobantes de pago — expediente por día / mes / año */}
      {!isTeacher && stmt && stmt.payments && stmt.payments.length > 0 && (() => {
        const now = new Date();
        const inPeriod = (p) => {
          if (recPeriod === "all") return true;
          if (!p.date) return false;
          const d = new Date(p.date);
          if (recPeriod === "day") return d.toDateString() === now.toDateString();
          if (recPeriod === "month") return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
          if (recPeriod === "year") return d.getFullYear() === now.getFullYear();
          return true;
        };
        const list = stmt.payments.filter((p) => inPeriod(p) && p.status !== "rejected");
        const total = list.reduce((s, p) => s + Number(p.net_amount || 0), 0);
        const PERIODS = [["all", "Todos"], ["day", "Hoy"], ["month", "Este mes"], ["year", "Este año"]];
        return (
        <div className="bg-white border border-nardo-200 rounded-lg p-5 sm:p-6 mb-4" data-testid="receipts-card">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2"><FileText className="w-4 h-4 text-navy" /><h3 className="font-display font-semibold text-lg text-navy">Comprobantes de pago · Expediente</h3></div>
            <div className="text-xs text-nardo-500">{list.length} comprobante{list.length === 1 ? "" : "s"} · <span className="text-emerald-700 font-semibold">{money(total)}</span></div>
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {PERIODS.map(([id, label]) => (
              <button key={id} onClick={() => setRecPeriod(id)} data-testid={`receipts-period-${id}`} className={`text-[11px] px-3 py-1.5 rounded-full transition-colors ${recPeriod === id ? "bg-navy text-white" : "bg-white text-nardo-600 border border-nardo-200 hover:border-navy"}`}>{label}</button>
            ))}
          </div>
          {(!stmt.tutors || stmt.tutors.filter((t) => t.whatsapp || t.email).length === 0) && (
            <p className="text-xs text-orange-600 mb-3">Agrega tutores con WhatsApp o correo (al crear el alumno) para poder enviar comprobantes.</p>
          )}
          {list.length === 0 ? (
            <p className="text-sm text-nardo-400 py-6 text-center" data-testid="receipts-empty">Sin comprobantes en este periodo.</p>
          ) : (
          <div className="divide-y divide-nardo-100">
            {list.map((p) => (
              <div key={p.folio} className="py-3 flex flex-wrap items-center justify-between gap-3" data-testid="receipt-row">
                <div>
                  <div className="text-sm text-navy font-medium">{p.concept}{p.period ? ` · ${p.period}` : ""} — {money(p.net_amount)}{p.status === "pending_verification" ? " (por verificar)" : ""}</div>
                  <div className="text-[11px] text-nardo-400 font-mono">{p.folio} · {p.date ? new Date(p.date).toLocaleDateString("es-MX") : ""} · {p.method || ""}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <a href={receiptUrl(p)} target="_blank" rel="noreferrer" data-testid="receipt-view" className="text-xs text-navy hover:underline inline-flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Ver</a>
                  {(stmt.tutors || []).map((t, ti) => (
                    <span key={ti} className="inline-flex items-center gap-1">
                      {t.whatsapp && <button onClick={() => sendReceiptWhatsApp(p, t)} data-testid="send-receipt-wa" title={`WhatsApp a ${t.name || t.whatsapp}`} className="inline-flex items-center gap-1 text-xs border border-emerald-300 text-emerald-700 hover:bg-emerald-50 px-2.5 py-1.5 rounded-md transition-colors"><MessageCircle className="w-3.5 h-3.5" /> {t.name?.split(" ")[0] || "WA"}</button>}
                      {t.email && <button onClick={() => sendReceiptEmail(p, t)} data-testid="send-receipt-mail" title={`Correo a ${t.email}`} className="inline-flex items-center text-xs border border-nardo-300 text-navy hover:border-navy px-2.5 py-1.5 rounded-md transition-colors"><Mail className="w-3.5 h-3.5" /></button>}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          )}
        </div>
        );
      })()}

      {/* Expediente por periodo */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 relative mb-4" data-testid="expediente-resumen-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-emerald-500" />
        <h3 className="font-display font-semibold text-lg text-navy mb-1 flex items-center gap-2">
          <FileText className="w-4 h-4 text-emerald-600" /> Expediente · resumen por periodo
        </h3>
        <p className="text-sm text-nardo-500 mb-4">Revisa la asistencia por periodo y descarga un resumen imprimible en PDF.</p>
        <div className="flex flex-wrap gap-2 mb-5">
          {[["hoy", "Hoy"], ["semana", "Esta semana"], ["mes", "Este mes"], ["ciclo", "Ciclo escolar"]].map(([key, label]) => (
            <button
              key={key}
              data-testid={`exp-period-${key}`}
              onClick={() => setExpPeriod(key)}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${expPeriod === key ? "bg-navy text-white" : "border border-navy/30 text-navy hover:bg-navy hover:text-white"}`}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="exp-summary">
          <div className="border border-nardo-100 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Días asistidos</div>
            <div className="font-display font-extrabold text-2xl text-emerald-700 mt-1" data-testid="exp-present">{expSummary.present}</div>
          </div>
          <div className="border border-nardo-100 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Retardos</div>
            <div className="font-display font-extrabold text-2xl text-orange-600 mt-1" data-testid="exp-late">{expSummary.late}</div>
          </div>
          <div className="border border-nardo-100 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Salidas anticipadas</div>
            <div className="font-display font-extrabold text-2xl text-violet-600 mt-1" data-testid="exp-early">{expSummary.early}</div>
          </div>
          <div className="border border-nardo-100 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Justificados</div>
            <div className="font-display font-extrabold text-2xl text-slate-600 mt-1" data-testid="exp-justified">{periodJustifs.length}</div>
          </div>
          <div className="border border-nardo-100 rounded-md p-3">
            <div className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Total registros</div>
            <div className="font-display font-extrabold text-2xl text-navy mt-1" data-testid="exp-records">{expSummary.records}</div>
          </div>
        </div>
        {periodJustifs.length > 0 && (
          <div className="border border-slate-200 bg-slate-50 rounded-md p-4 mb-5" data-testid="exp-justifs-list">
            <div className="text-[11px] uppercase tracking-[0.2em] text-slate-600 font-semibold mb-2">Justificantes del periodo</div>
            <div className="divide-y divide-slate-200">
              {periodJustifs.map((j) => (
                <div key={j.id} className="py-2 flex items-start gap-3 text-sm">
                  <span className="font-mono text-slate-700 whitespace-nowrap">{j.date}</span>
                  <span className="text-nardo-600">{j.reason || "Sin motivo especificado"}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        <button
          data-testid="exp-download"
          onClick={downloadExpediente}
          disabled={expBusy}
          className="inline-flex items-center gap-2 bg-navy hover:bg-navy-light text-white px-5 py-2.5 rounded-md transition-colors text-sm font-medium disabled:opacity-50"
        >
          {expBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Descargar expediente PDF
        </button>
      </div>

      {/* Monthly PDF downloads (current year) */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 relative mb-4" data-testid="profile-monthly-pdf-card">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
        <h3 className="font-display font-semibold text-lg text-navy mb-1 flex items-center gap-2">
          <Download className="w-4 h-4" /> Descargar PDF por mes · {new Date().getFullYear()}
        </h3>
        <p className="text-sm text-nardo-500 mb-5">Cada PDF incluye los colores: <span className="text-emerald-700 font-medium">a tiempo</span>, <span className="text-orange-600 font-medium">retardo</span>, <span className="text-blue-600 font-medium">salida</span>, <span className="text-violet-600 font-medium">salida anticipada</span> y <span className="text-red-600 font-medium">no asistió</span>.</p>
        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2">
          {Array.from({ length: 12 }).map((_, m) => {
            const year = new Date().getFullYear();
            const ym = `${year}-${String(m + 1).padStart(2, "0")}`;
            const isFuture = m > new Date().getMonth();
            const MONTHS_FULL = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
            return (
              <button
                key={ym}
                data-testid={`profile-month-pdf-${ym}`}
                onClick={() => downloadMonth(ym)}
                disabled={isFuture || !!busyMonth}
                title={isFuture ? "Mes futuro" : `Descargar ${MONTHS_FULL[m]} ${year}`}
                className="inline-flex items-center justify-center gap-1.5 border border-nardo-200 text-navy hover:border-navy hover:bg-nardo-50 px-2 py-2 rounded-md transition-colors text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {busyMonth === ym ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                {MONTHS_ES[m]}
              </button>
            );
          })}
        </div>
      </div>

      {/* Heatmap */}
      <div className="bg-white border border-nardo-200 rounded-md p-6 relative mb-4">
        <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy-light" />
        <h3 className="font-display font-semibold text-lg text-navy mb-6 flex items-center gap-2">
          <CalendarDays className="w-4 h-4" /> Mapa de asistencia · últimos 365 días
        </h3>
        <Heatmap daysMap={daysMap} />
      </div>

      {/* Daily table */}
      <div className="bg-white border border-nardo-200 rounded-md">
        <div className="px-6 py-4 border-b border-nardo-200 flex items-center justify-between flex-wrap gap-3">
          <h3 className="font-display font-semibold text-lg text-navy">Registro diario de entradas y salidas</h3>
          <select
            data-testid="profile-month-filter"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="border border-nardo-200 rounded-md px-3 py-1.5 text-sm text-navy bg-white outline-none focus:border-navy"
          >
            <option value="">Todos los meses</option>
            {monthOptions.map((m) => {
              const [y, mm] = m.split("-");
              return <option key={m} value={m}>{MONTHS_ES[parseInt(mm, 10) - 1]} {y}</option>;
            })}
          </select>
        </div>
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-nardo-200 bg-nardo-50/50 text-[10px] uppercase tracking-[0.25em] text-nardo-500">
          <div className="col-span-5">Fecha</div>
          <div className="col-span-3">Entrada</div>
          <div className="col-span-3">Salida</div>
          <div className="col-span-1 text-right">Reg.</div>
        </div>
        {filteredDays.length === 0 && (
          <div className="px-6 py-16 text-center text-nardo-500 text-sm" data-testid="profile-no-records">Sin registros para este periodo.</div>
        )}
        {filteredDays.map((d) => {
          const dt = new Date(d.date + "T00:00:00");
          const label = `${WEEKDAYS_ES[dt.getDay()]} ${dt.getDate()} ${MONTHS_ES[dt.getMonth()]} ${dt.getFullYear()}`;
          return (
            <div key={d.date} className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-nardo-100 items-center text-sm" data-testid="profile-day-row">
              <div className="col-span-5 text-navy font-medium">{label}</div>
              <div className={`col-span-3 font-mono ${d.first_in ? (isLate(d.first_in, lateThreshold) ? "text-orange-600" : "text-emerald-700") : "text-navy"}`} title={d.first_in && isLate(d.first_in, lateThreshold) ? "Entrada con retardo" : "Entrada"}>{fmtTime(d.first_in)}</div>
              <div className={`col-span-3 font-mono ${d.last_out ? (isEarly(d.last_out, exitThreshold) ? "text-violet-600" : "text-blue-600") : "text-navy"}`} title={d.last_out && isEarly(d.last_out, exitThreshold) ? "Salida anticipada" : "Salida"}>{fmtTime(d.last_out)}</div>
              <div className="col-span-1 text-right font-mono text-nardo-600">{d.count}</div>
            </div>
          );
        })}
      </div>

      {editOpen && (
        <div className="fixed inset-0 z-50 bg-navy/40 backdrop-blur-sm flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={() => setEditOpen(false)}>
          <div className="bg-white border border-nardo-200 rounded-md w-full max-w-lg my-auto max-h-[90vh] overflow-y-auto p-8 relative animate-slide-up" onClick={(e) => e.stopPropagation()} data-testid="edit-info-modal">
            <button onClick={() => setEditOpen(false)} className="absolute top-4 right-4 text-nardo-500 hover:text-navy" data-testid="edit-info-close"><X className="w-4 h-4" /></button>
            <div className="absolute top-0 left-0 h-[2px] w-16 bg-navy" />
            <h2 className="font-display font-bold text-2xl text-navy mb-1">Editar información {isTeacher ? "del docente" : "del alumno"}</h2>
            <p className="text-sm text-nardo-500 mb-6">Actualiza los datos y los contactos para avisos.</p>
            <div className="space-y-5">
              <div>
                <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nombre completo</label>
                <input value={ef.full_name} onChange={(e) => setEf((s) => ({ ...s, full_name: e.target.value }))} data-testid="edit-info-name"
                  className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy" />
              </div>
              {!isTeacher && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Nivel de estudios</label>
                  <select value={ef.level} onChange={(e) => setEf((s) => ({ ...s, level: e.target.value }))} data-testid="edit-info-level"
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                    <option value="">Selecciona un nivel…</option>
                    {LEVELS.map((lv) => <option key={lv} value={lv}>{lv}</option>)}
                  </select>
                </div>
              )}
              {!isTeacher && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Tipo de sangre</label>
                  <select value={ef.blood_type} onChange={(e) => setEf((s) => ({ ...s, blood_type: e.target.value }))} data-testid="edit-info-blood"
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                    <option value="">Sin especificar</option>
                    {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                  <p className="text-[11px] text-nardo-400 mt-1">Aparece automáticamente en la credencial.</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-5">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">{isTeacher ? "Materia / Cargo" : "Grado"}</label>
                  <input value={ef.grade} onChange={(e) => setEf((s) => ({ ...s, grade: e.target.value }))} data-testid="edit-info-grade"
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">UID NFC</label>
                  <input value={ef.nfc_uid} onChange={(e) => setEf((s) => ({ ...s, nfc_uid: e.target.value }))} data-testid="edit-info-uid"
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none font-mono" placeholder="04 A1 9F 22" />
                </div>
              </div>
              {isTeacher && (
                <div>
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">WhatsApp (para comunicados)</label>
                  <input value={ef.whatsapp} onChange={(e) => setEf((s) => ({ ...s, whatsapp: e.target.value }))} data-testid="edit-info-whatsapp" inputMode="tel" placeholder="Ej. 52 55 1234 5678 (con lada país)"
                    className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none font-mono" />
                  <p className="text-[11px] text-nardo-400 mt-1">Administración y Dirección podrán enviarle comunicados por WhatsApp.</p>
                </div>
              )}
              {!isTeacher && (
                <div className="space-y-3 border border-nardo-200 rounded-md p-3" data-testid="edit-info-billing">
                  <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500 font-semibold">Plan de pago y colegiatura</label>
                  {hasAnyPlans(user) && (
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Plan de pago</label>
                      <select value={ef.plan} onChange={(e) => setEf((s) => ({ ...s, plan: e.target.value }))} data-testid="edit-info-plan"
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                        <option value="">Sin plan (colegiatura manual)</option>
                        {PLAN_KEYS.map((k) => { const amt = planAmount(user, ef.level, k, ef.grade); return (
                          <option key={k} value={k}>{PLAN_LABELS[k]}{amt > 0 ? ` · $${amt.toLocaleString("es-MX")}/mes` : ""}</option>
                        ); })}
                      </select>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Colegiatura</label>
                      <input type="number" min="0" step="0.01" data-testid="edit-info-monthly"
                        value={ef.plan ? (planAmount(user, ef.level, ef.plan, ef.grade) || "") : ef.monthly_fee} disabled={!!ef.plan}
                        onChange={(e) => setEf((s) => ({ ...s, monthly_fee: e.target.value }))}
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy disabled:text-nardo-400" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Inscripción</label>
                      <input type="number" min="0" step="0.01" data-testid="edit-info-enrollment"
                        value={ef.enrollment_fee} onChange={(e) => setEf((s) => ({ ...s, enrollment_fee: e.target.value }))}
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy" />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.2em] text-nardo-500">Beca</label>
                      <select value={ef.schId} onChange={(e) => setEf((s) => ({ ...s, schId: e.target.value }))} data-testid="edit-info-scholarship"
                        className="mt-1 w-full border-b-2 border-nardo-200 bg-transparent py-2 focus:border-navy outline-none text-navy">
                        <option value="">Sin beca</option>
                        {scholarships.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.pct}%)</option>)}
                      </select>
                    </div>
                  </div>
                  {(() => {
                    const sch = scholarships.find((s) => s.id === ef.schId);
                    const base = ef.plan ? (planAmount(user, ef.level, ef.plan, ef.grade) || 0) : (Number(ef.monthly_fee) || 0);
                    const pct = sch ? Number(sch.pct) || 0 : 0;
                    if (!(pct > 0 && base > 0)) return null;
                    return (
                      <p className="text-[12px] text-emerald-700 font-medium" data-testid="edit-info-effective-fee">
                        Colegiatura con beca ({pct}%): ${(base - base * pct / 100).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / mes
                      </p>
                    );
                  })()}
                  <p className="text-[11px] text-nardo-400">
                    {ef.plan ? "La colegiatura se toma del plan según el nivel y grado del alumno. " : ""}
                    {scholarships.length === 0 ? "Configura las becas en Ajustes → Cobranza y pagos para elegirlas aquí." : "Al elegir una beca, su porcentaje se aplica automáticamente sobre la colegiatura."}
                  </p>
                </div>
              )}
              {!isTeacher && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <label className="text-[10px] uppercase tracking-[0.25em] text-nardo-500">Tutores (WhatsApp / correo para avisos)</label>
                    <button type="button" onClick={eAddTutor} data-testid="edit-add-tutor" className="text-xs text-navy hover:underline inline-flex items-center gap-1"><Plus className="w-3 h-3" /> Agregar tutor</button>
                  </div>
                  {eTutors.length === 0 && <p className="text-xs text-nardo-400">Sin tutores. Agrega uno para enviar avisos y comprobantes.</p>}
                  {eTutors.map((t, i) => (
                    <div key={i} className="border border-nardo-200 rounded-md p-3 space-y-2" data-testid={`edit-tutor-row-${i}`}>
                      <div className="flex items-center gap-2">
                        <input value={t.name} onChange={(e) => eUpdateTutor(i, "name", e.target.value)} placeholder="Nombre del tutor" data-testid={`edit-tutor-name-${i}`}
                          className="flex-1 border-b border-nardo-200 bg-transparent py-1.5 text-sm focus:border-navy outline-none" />
                        <select value={t.relation || ""} onChange={(e) => eUpdateTutor(i, "relation", e.target.value)} data-testid={`edit-tutor-relation-${i}`}
                          className="border-b border-nardo-200 bg-transparent py-1.5 text-sm focus:border-navy outline-none text-navy">
                          <option value="">Parentesco…</option>
                          {["Mamá", "Papá", "Tutor(a)", "Abuelo(a)", "Otro"].map((r) => <option key={r} value={r}>{r}</option>)}
                        </select>
                        <button type="button" onClick={() => eRemoveTutor(i)} data-testid={`edit-tutor-remove-${i}`} className="text-nardo-400 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <input value={t.whatsapp} onChange={(e) => eUpdateTutor(i, "whatsapp", e.target.value)} placeholder="WhatsApp (con lada)" inputMode="tel" data-testid={`edit-tutor-whatsapp-${i}`}
                          className="border-b border-nardo-200 bg-transparent py-1.5 text-sm font-mono focus:border-navy outline-none" />
                        <input value={t.email} onChange={(e) => eUpdateTutor(i, "email", e.target.value)} placeholder="Correo" type="email" data-testid={`edit-tutor-email-${i}`}
                          className="border-b border-nardo-200 bg-transparent py-1.5 text-sm focus:border-navy outline-none" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <button onClick={saveEdit} disabled={savingEdit} data-testid="edit-info-save"
                className="w-full bg-navy hover:bg-navy-light text-white py-3 rounded-md transition-colors font-medium disabled:opacity-50">
                {savingEdit ? "Guardando…" : "Guardar cambios"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
